import React, { useEffect, useState } from 'react';

import { Row, Col, Card, CardBody, Collapse } from 'reactstrap';

import { api } from '../../config';
import {
  QuantityDeliverySection,
  useQuantityDeliveries,
} from '../../Components/QuantityTable';
import { PortRestrictionsTable } from '../../Components/PortRestriction';
import {
  getContractPeriodProps,
  isLpgProduct,
} from '../TenderOpening/utils/tenderOpeningHelpers';
import '../../App.css';
import { formatDateTime } from '../../utils/format';
import { getProcurementProductTypeNames } from '../../shared/offer-domain/constants';
import { isProcurementClauseActive } from '../../shared/offer-domain/respond/helpers';

const HtmlContent = ({ value, framed = true }) => {
  if (!value) {
    return <div className='text-dark mb-0 lh-base'>-</div>;
  }

  const body = (
    <div
      className='html-content-body'
      dangerouslySetInnerHTML={{ __html: value }}
    />
  );

  if (!framed) return body;

  return <div className='html-content-panel'>{body}</div>;
};

const MandatoryBadge = () => (
  <span className='badge bg-danger-subtle text-danger border border-danger-subtle rounded-pill fw-medium fs-11'>
    Mandatory
  </span>
);

const ClauseBlock = ({ index, title, value, isMandatory }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div
      className={`rounded-3 overflow-hidden mb-2 ${
        isExpanded
          ? 'border border-primary shadow-sm bg-white'
          : 'border bg-white'
      }`}
    >
      <button
        type='button'
        className='w-100 border-0 bg-transparent text-start px-3 py-3 d-flex align-items-center gap-3'
        onClick={() => setIsExpanded(prev => !prev)}
        aria-expanded={isExpanded}
      >
        <span
          className={`rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 fw-semibold fs-12 ${
            isExpanded ? 'bg-primary text-white' : 'bg-light text-muted border'
          }`}
          style={{ width: '28px', height: '28px' }}
        >
          {index}
        </span>

        <div className='flex-grow-1 min-w-0'>
          <div className='d-flex align-items-center gap-2 flex-wrap'>
            <span
              className={`fs-14 mb-0 ${
                isExpanded ? 'fw-bold text-dark' : 'fw-semibold text-dark'
              }`}
            >
              {title}
            </span>
            {isMandatory && <MandatoryBadge />}
          </div>
          {!isExpanded && (
            <span className='text-muted fs-12'>
              Click to view clause content
            </span>
          )}
        </div>

        <span
          className={`rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 ${
            isExpanded
              ? 'bg-primary-subtle text-primary'
              : 'bg-light text-muted'
          }`}
          style={{ width: '28px', height: '28px' }}
        >
          <i
            className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line fs-16`}
          ></i>
        </span>
      </button>

      <Collapse isOpen={isExpanded}>
        <div className='px-3 pb-3'>
          <div className='ms-1 ps-4'>
            {isMandatory && (
              <div className='text-danger small fw-medium mb-2'>
                * Mandatory clause for DMUT to fulfill
              </div>
            )}
            <div className='p-3 rounded-3 bg-light-subtle border text-muted fs-13 lh-base'>
              <HtmlContent value={value} framed={false} />
            </div>
          </div>
        </div>
      </Collapse>
    </div>
  );
};

const getVisibleProcurementClauses = (form = {}) =>
  [...(form.procurementClauseDetails ?? [])]
    .filter(isProcurementClauseActive)
    .sort((a, b) => (a.ordreNumber ?? 0) - (b.ordreNumber ?? 0));

const RequirementTab = ({ form, error, ids, token }) => {
  const { parcels, isLoading: isLoadingParcels } = useQuantityDeliveries(ids);

  const [productName, setProductName] = useState({ items: [] });
  const [productTypeData, setProductTypeData] = useState({ items: [] });
  const [currencyName, setCurrencyName] = useState({ items: [] });
  const [invoiceQuantityData, setInvoiceQuantityData] = useState({ items: [] });
  const [modelinvoiceQuantityData, setModelInvoiceQuantityData] = useState({
    items: [],
  });
  const [lawData, setLawData] = useState({ items: [] });
  const [abitrationData, setAbitrationData] = useState({ items: [] });

  useEffect(() => {
    fetchProductName();
    fetchCurrencyName();
    fetchInvoiceQuantity();
    fetchLaw();
    fetchAbitration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (form.invoicingQuantityId) {
      fetchModeInvoiceQuantity(form.invoicingQuantityId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.invoicingQuantityId]);

  useEffect(() => {
    if (form.productNameId) {
      fetchProductType(form.productNameId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.productNameId]);

  const fetchProductName = async () => {
    try {
      const response = await fetch(`${api.API_URL}/product`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();
      const items = result.data.items.map(item => ({
        value: item.id,
        label: item.name,
      }));
      setProductName({ items });
    } catch {
      void 0;
    }
  };

  const fetchCurrencyName = async () => {
    try {
      const response = await fetch(`${api.API_URL}/paymentcurrency`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();
      const items = (result?.data?.items || result?.data || result || [])
        .map(item => ({
          value: item?.id || '',
          label:
            item?.currencyName + ' (' + item.currencyCode + ')' ||
            item?.name ||
            'Unknown Currency',
        }))
        .filter(item => item.value && item.label);

      setCurrencyName({ items });
    } catch {
      setCurrencyName({ items: [] });
    }
  };

  const fetchLaw = async () => {
    try {
      const response = await fetch(`${api.API_URL}/law`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();
      const items = result.data.items.map(item => ({
        value: item.id,
        label: item.name,
      }));
      setLawData({ items });
    } catch {
      void 0;
    }
  };

  const fetchInvoiceQuantity = async () => {
    try {
      const response = await fetch(`${api.API_URL}/invoicing-quantity`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();
      const items = result.data.items.map(item => ({
        value: item.id,
        label: item.name,
      }));
      setInvoiceQuantityData({ items });
    } catch {
      void 0;
    }
  };

  const fetchModeInvoiceQuantity = async invoicingQuantityId => {
    try {
      const response = await fetch(
        `${api.API_URL}/invoicing-quantity/getAll-ModeInvoicingQuantity?InvoicingQuantityId=${invoicingQuantityId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const result = await response.json();
      const list = result?.data?.items || result?.data || [];
      const items = (Array.isArray(list) ? list : []).map(item => ({
        value: item.id,
        label: item.name,
      }));
      setModelInvoiceQuantityData({ items });
    } catch {
      setModelInvoiceQuantityData({ items: [] });
    }
  };

  const fetchAbitration = async () => {
    try {
      const response = await fetch(`${api.API_URL}/law/getAll-arbitration`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();
      const items = result.data.items.map(item => ({
        value: item.id,
        label: item.name,
      }));
      setAbitrationData({ items });
    } catch {
      void 0;
    }
  };

  const fetchProductType = async productTypeId => {
    try {
      const response = await fetch(
        `${api.API_URL}/product/getAll-product-type?ProductId=${productTypeId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const result = await response.json();
      const items = result.data.items.map(item => ({
        value: item.id,
        label: item.name,
      }));
      setProductTypeData({ items });
    } catch {
      void 0;
    }
  };

  const getLabel = (items, value, entity) => {
    const fromEntity = entity?.name ?? entity?.Name;
    if (fromEntity) return fromEntity;
    if (!items || !value) return '-';
    return items.find(obj => String(obj.value) === String(value))?.label || '-';
  };

  const periodProps = getContractPeriodProps(form);
  const visibleClauses = getVisibleProcurementClauses(form);

  return (
    <React.Fragment>
      <Row>
        <Card className='mb-4 border shadow-sm'>
          <CardBody>
            <Row>
              <Col xs={12}>
                <div className='mb-4'>
                  <h5 className='fw-bold text-dark mb-1'>Invitation Detail</h5>
                  <p className='text-muted small'>
                    Bisa jadi Kalimat tambahan untuk membantu section mengenai
                    apa ini
                  </p>
                </div>

                <hr className='my-2 border-secondary' />

                <div className='mb-4'>
                  <h5 className='fw-semibold text-dark mb-1'>Contract</h5>
                  <p className='text-muted small mb-2'>
                    Bisa jadi Kalimat tambahan untuk membantu section mengenai
                    apa ini
                  </p>

                  <Row className='g-3'>
                    <Col xs={6} md={3}>
                      <div className='border-start border-2 border-primary ps-3'>
                        <div className='text-muted small fw-semibold mb-2'>
                          Contract Type
                        </div>
                        <div className='text-dark fs-13 mb-0 lh-base'>
                          {parseInt(form?.contractType ?? 0, 10) === 1
                            ? 'TERM'
                            : 'SPOT'}
                        </div>
                      </div>
                    </Col>

                    <Col xs={6} md={3}>
                      <div className='border-start border-2 border-primary ps-3'>
                        <div className='text-muted small fw-semibold mb-2'>
                          {periodProps.startLabel}
                        </div>
                        <div className='text-dark fs-13 mb-0 lh-base'>
                          {periodProps.startValue}
                        </div>
                      </div>
                    </Col>

                    {periodProps.endLabel && (
                      <Col xs={6} md={3}>
                        <div className='border-start border-2 border-primary ps-3'>
                          <div className='text-muted small fw-semibold mb-2'>
                            {periodProps.endLabel}
                          </div>
                          <div className='text-dark fs-13 mb-0 lh-base'>
                            {periodProps.endValue}
                          </div>
                        </div>
                      </Col>
                    )}

                    <Col xs={6} md={3}>
                      <div className='border-start border-2 border-primary ps-3'>
                        <div className='text-muted small fw-semibold mb-2'>
                          Procurement No
                        </div>
                        <div className='text-dark fs-13 mb-0 lh-base'>
                          {form.procurementNumber || '-'}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>

                <div className='mb-4'>
                  <h5 className='fw-semibold text-dark mb-1'>
                    Product Specification / Quantity
                  </h5>
                  <p className='text-muted small mb-2'>
                    Bisa jadi Kalimat tambahan untuk membantu section mengenai
                    apa ini
                  </p>

                  <Row className='g-3'>
                    <Col xs={12} md={3}>
                      <div className='border-start border-2 border-primary ps-3'>
                        <div className='text-muted small fw-semibold mb-2'>
                          Product Name
                        </div>
                        <div className='text-dark fs-13 mb-0 lh-base'>
                          {productName.items?.find(
                            obj => obj.value === form.productNameId
                          )?.label || '-'}
                        </div>
                      </div>
                    </Col>
                    <Col xs={12} md={3}>
                      <div className='border-start border-2 border-primary ps-3'>
                        <div className='text-muted small fw-semibold mb-2'>
                          Product Type
                        </div>
                        <div className='text-dark fs-13 mb-0 lh-base'>
                          {getProcurementProductTypeNames(form) ||
                            productTypeData.items?.find(
                              obj => obj.value === form.productTypeId
                            )?.label ||
                            '-'}
                        </div>
                      </div>
                    </Col>
                    <Col xs={12} md={3}>
                      <div className='border-start border-2 border-primary ps-3'>
                        <div className='text-muted small fw-semibold mb-2'>
                          Operational Tolerance
                        </div>
                        <div className='text-dark fs-13 mb-0 lh-base'>
                          {form.operationalTolerance + '%' || '-'}
                        </div>
                      </div>
                    </Col>
                    <Col xs={12} md={3}>
                      <div className='border-start border-2 border-primary ps-3'>
                        <div className='text-muted small fw-semibold mb-2'>
                          Operation Tolerance Options
                        </div>
                        <div className='text-dark fs-13 mb-0 lh-base'>
                          {form.partyType || '-'}
                        </div>
                      </div>
                    </Col>
                    <Col xs={12} md={12}>
                      <div className='border-start border-2 border-primary ps-3'>
                        <div className='text-muted small fw-semibold mb-2'>
                          Product Spesification
                        </div>
                        <HtmlContent value={form.productSpecification} />
                      </div>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
            <hr className='my-2 border-secondary' />
            <Row>
              <Col sm={12}>
                <h5 className='fw-semibold text-dark mb-1'>
                  Quantity & Delivery Time
                </h5>
                <p className='text-muted small mb-3'>
                  Bisa jadi Kalimat tambahan untuk membantu section mengenai apa
                  ini
                </p>
              </Col>

              <Col xl={12}>
                <div className='mb-1'>
                  <QuantityDeliverySection
                    parcels={parcels}
                    isLoading={isLoadingParcels}
                    error={error}
                    isLpg={isLpgProduct(form)}
                    quantityUnitLabel={form?.productType?.unit}
                    emptyMessage='No data available'
                  />

                  <hr className='my-4 border-secondary border-dashed' />

                  <Col md={12}>
                    <div className='border-start border-2 border-primary ps-3'>
                      <div className='text-muted small fw-semibold mb-2'>
                        Notes
                      </div>
                      <HtmlContent value={form?.globalNotes} />
                    </div>
                  </Col>
                  <hr className='my-4 border-secondary border-dashed' />
                  <Col md={12}>
                    <div className='mt-2'>
                      <h5 className='fw-bold text-dark mb-3'>
                        Port Restrictions
                      </h5>
                      <PortRestrictionsTable
                        ports={form?.portRestriction}
                        productId={form?.productNameId}
                      />
                    </div>
                  </Col>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card className='mb-4 border shadow-sm'>
          <CardBody>
            <div className='d-flex align-items-start justify-content-between gap-3 mb-4'>
              <div>
                <h5 className='fw-bold text-dark mb-1'>Clause Details</h5>
                <p className='text-muted small mb-0'>
                  Review procurement clauses and requirements for this
                  invitation
                </p>
              </div>
              <span className='badge bg-primary-subtle text-primary rounded-pill fw-medium fs-12 flex-shrink-0'>
                {visibleClauses.length} clauses
              </span>
            </div>

            {visibleClauses.length > 0 ? (
              <div className='d-flex flex-column'>
                {visibleClauses.map((clause, index) => (
                  <ClauseBlock
                    key={clause.id}
                    index={index + 1}
                    title={clause.clauseTitle}
                    value={clause.clauseDescription}
                    isMandatory={clause.isShowToDMUT === true}
                  />
                ))}
              </div>
            ) : (
              <div className='text-center py-4 text-muted border rounded-3 bg-light-subtle'>
                No clause data available.
              </div>
            )}
          </CardBody>
        </Card>

        <Card className='mb-4 border shadow-sm'>
          <CardBody>
            <Row>
              <Col xs={12}>
                <div className='mb-4'>
                  <h5 className='fw-bold text-dark mb-1'>Payment Terms</h5>
                  <p className='text-muted small'>
                    Bisa jadi Kalimat tambahan untuk membantu section mengenai
                    apa ini
                  </p>

                  <Row className='g-3'>
                    <Col xs={6} md={3}>
                      <div className='border-start border-2 border-primary ps-3'>
                        <div className='text-muted small fw-semibold mb-2'>
                          Invoicing Quantity
                        </div>
                        <div className='text-dark fs-13 mb-0 lh-base'>
                          {getLabel(
                            invoiceQuantityData.items,
                            form.invoicingQuantityId,
                            form.invoicingQuantity
                          )}
                        </div>
                      </div>
                    </Col>
                    <Col xs={6} md={3}>
                      <div className='border-start border-2 border-primary ps-3'>
                        <div className='text-muted small fw-semibold mb-2'>
                          Mode
                        </div>
                        <div className='text-dark fs-13 mb-0 lh-base'>
                          {getLabel(
                            modelinvoiceQuantityData.items,
                            form.modeInvoicingQuantityId,
                            form.modeInvoicingQuantity
                          )}
                        </div>
                      </div>
                    </Col>
                    <Col xs={6} md={3}>
                      <div className='border-start border-2 border-primary ps-3'>
                        <div className='text-muted small fw-semibold mb-2'>
                          Currency
                        </div>
                        <div className='text-dark fs-13 mb-0 lh-base'>
                          {getLabel(currencyName.items, form.paymentCurrencyId)}
                        </div>
                      </div>
                    </Col>
                    <Col xs={6} md={12}>
                      <div className='border-start border-2 border-primary ps-3'>
                        <div className='text-muted small fw-semibold mb-2'>
                          Description of Payment Term
                        </div>
                        <HtmlContent value={form.descriptionOfPaymentTerms} />
                      </div>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
            <hr className='my-2 border-secondary' />
            <Row>
              <Col xs={12}>
                <div className='mb-4'>
                  <h5 className='fw-bold text-dark mb-1'>Compliance</h5>
                  <p className='text-muted small'>
                    Bisa jadi Kalimat tambahan untuk membantu section mengenai
                    apa ini
                  </p>

                  <Row className='g-3'>
                    <Col xs={6} md={6}>
                      <div className='border-start border-2 border-primary ps-3'>
                        <div className='text-muted small fw-semibold mb-2'>
                          Law
                        </div>
                        <div className='text-dark fs-13 mb-0 lh-base'>
                          {getLabel(lawData.items, form.lawId)}
                        </div>
                      </div>
                    </Col>
                    <Col xs={6} md={6}>
                      <div className='border-start border-2 border-primary ps-3'>
                        <div className='text-muted small fw-semibold mb-2'>
                          Arbitration
                        </div>
                        <div className='text-dark fs-13 mb-0 lh-base'>
                          {getLabel(abitrationData.items, form.arbitrationId)}
                        </div>
                      </div>
                    </Col>
                    <Col xs={6} md={12}>
                      <div className='border-start border-2 border-primary ps-3'>
                        <div className='text-muted small fw-semibold mb-2'>
                          Other Terms & Condition
                        </div>
                        <HtmlContent value={form.otherTermsAndCondition} />
                      </div>
                    </Col>
                    <Col xs={6} md={12}>
                      <div className='border-start border-2 border-primary ps-3'>
                        <div className='text-muted small fw-semibold mb-2'>
                          Additional Notes
                        </div>
                        <HtmlContent value={form.additionalNotes} />
                      </div>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card className='border shadow-sm'>
          <CardBody>
            <Row>
              <Col xs={12}>
                <div>
                  <h5 className='fw-bold text-dark mb-1'>Validity</h5>
                  <p className='text-muted small'>
                    Bisa jadi Kalimat tambahan untuk membantu section mengenai
                    apa ini
                  </p>

                  <Row className='g-3'>
                    <Col xs={6} md={6}>
                      <div className='border-start border-2 border-primary ps-3'>
                        <div className='text-muted small fw-semibold mb-2'>
                          Submission Deadline (Jakarta Local Time)
                        </div>
                        <div className='text-dark fs-13 mb-0 lh-base'>
                          {form.proposalSubmissionDeadline
                            ? formatDateTime(form.proposalSubmissionDeadline)
                            : '-'}
                        </div>
                      </div>
                    </Col>
                    <Col xs={6} md={6}>
                      <div className='border-start border-2 border-primary ps-3'>
                        <div className='text-muted small fw-semibold mb-2'>
                          Validity Period (Jakarta Local Time)
                        </div>
                        <div className='text-dark fs-13 mb-0 lh-base'>
                          {form.proposalValidityPeriodMax
                            ? formatDateTime(form.proposalValidityPeriodMax)
                            : '-'}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Row>
    </React.Fragment>
  );
};

export default RequirementTab;
